import { useState } from 'react';
import { TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { tradingAPI } from '../../services/api';

import { fmtFixed, fmtNumber } from '../../utils/format';
const OrderForm = ({ selectedPair = 'BTC/USDT', currentPrice = 43250.50 }) => {
  const [orderType, setOrderType] = useState('market');
  const [side, setSide] = useState('buy');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState(currentPrice);
  const [total, setTotal] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAmountChange = (value) => {
    setAmount(value);
    if (orderType === 'limit') {
      setTotal(value * price);
    } else {
      setTotal(value * currentPrice);
    }
  };

  const handlePriceChange = (value) => {
    setPrice(value);
    setTotal(amount * value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (orderType === 'limit' && (!price || parseFloat(price) <= 0)) {
      toast.error('Please enter a valid limit price');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const orderData = {
        pair: selectedPair,
        side: side,
        orderType: orderType,
        amount: parseFloat(amount),
        price: orderType === 'limit' ? parseFloat(price) : null,
      };

      const result = await tradingAPI.placeOrder(orderData);
      
      toast.success(
        `${side.toUpperCase()} order placed successfully!\n${amount} ${selectedPair.split('/')[0]} @ $${fmtNumber(result.executed_price || price, 2)}`,
        { duration: 4000 }
      );
      
      setAmount('');
      setTotal(0);
      
    } catch (error) {
      console.error('Order placement error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to place order';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="glow-card p-4 sm:p-6 rounded-xl">
      <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">Place Order</h3>
      
      {/* Buy/Sell Toggle */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <button
          onClick={() => setSide('buy')}
          className={`py-3 rounded-lg font-bold transition-all flex items-center justify-center space-x-2 ${
            side === 'buy'
              ? 'bg-green-500 text-white shadow-lg'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          <TrendingUp className="h-5 w-5" />
          <span>Buy</span>
        </button>
        <button
          onClick={() => setSide('sell')}
          className={`py-3 rounded-lg font-bold transition-all flex items-center justify-center space-x-2 ${
            side === 'sell'
              ? 'bg-red-500 text-white shadow-lg'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          <TrendingDown className="h-5 w-5" />
          <span>Sell</span>
        </button>
      </div>

      {/* Order Type */}
      <div className="mb-6">
        <label className="text-sm text-gray-400 mb-2 block">Order Type</label>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setOrderType('market')}
            className={`py-2 rounded-lg font-semibold transition-all ${
              orderType === 'market'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Market
          </button>
          <button
            onClick={() => setOrderType('limit')}
            className={`py-2 rounded-lg font-semibold transition-all ${
              orderType === 'limit'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Limit
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Price (for limit orders) */}
        {orderType === 'limit' && (
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Price (USDT)</label>
            <input
              type="number"
              value={price}
              onChange={(e) => handlePriceChange(parseFloat(e.target.value))}
              className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              step="0.01"
            />
          </div>
        )}

        {/* Amount */}
        <div>
          <label className="text-sm text-gray-400 mb-2 block">Amount ({selectedPair.split('/')[0]})</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => handleAmountChange(parseFloat(e.target.value))}
            placeholder="0.00"
            className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            step="0.00001"
          />
        </div>

        {/* Quick Amount Buttons */}
        <div className="grid grid-cols-4 gap-2">
          {['25%', '50%', '75%', '100%'].map((percent) => (
            <button
              key={percent}
              type="button"
              onClick={() => handleAmountChange((parseFloat(percent) / 100) * 10)}
              className="py-2 bg-gray-800 text-gray-400 rounded-lg hover:bg-gray-700 text-sm font-semibold"
            >
              {percent}
            </button>
          ))}
        </div>

        {/* Total */}
        <div className="bg-gray-900/50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Total</span>
            <div className="flex items-center space-x-1 text-xl font-bold text-yellow-400">
              <DollarSign className="h-5 w-5" />
              <span>{fmtFixed(total, 2)}</span>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            Available: $125,430.50 USDT
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-4 rounded-lg font-bold text-lg transition-all shadow-lg ${
            isSubmitting
              ? 'bg-gray-600 cursor-not-allowed text-gray-300'
              : side === 'buy'
              ? 'bg-green-500 hover:bg-green-600 text-white'
              : 'bg-red-500 hover:bg-red-600 text-white'
          }`}
        >
          {isSubmitting ? 'Placing Order...' : `${side === 'buy' ? 'Buy' : 'Sell'} ${selectedPair.split('/')[0]}`}
        </button>
      </form>
    </div>
  );
};

export default OrderForm;
